import * as D from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import {
  Banknote, Boxes, BriefcaseBusiness, Building2, ClipboardList, Factory, FileText, Hash, Landmark, Loader2, type LucideIcon, Package, Plus, Receipt, ScrollText, Search, ShoppingCart, Store, Target, Users, Warehouse,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { http } from '@/lib/api/http';
import { can, useAuth } from '@/lib/auth/auth';
import { useDebounced } from '@/lib/use-debounced';
import { NAV, groupLinks } from './nav';
import { useModuleFilter } from './use-modules';

interface Hit { type: string; id: string; title: string; subtitle: string | null; status: string | null; date: string | null }
interface SearchResult { q: string; groups: Array<{ type: string; items: Hit[] }> }

const ICONS: Record<string, LucideIcon> = {
  party: Users, product: Package, sales_document: FileText, purchase_document: ShoppingCart, payment: Banknote, purchase_request: ClipboardList, rfq: ClipboardList,
  journal_entry: ScrollText, account: Hash, project: BriefcaseBusiness, lead: Target, opportunity: Target, asset: Building2, instrument: Landmark, supplier_instrument: Landmark,
  lot: Boxes, serial: Boxes, warehouse: Warehouse, work_order: Factory, pos_ticket: Store,
};
/** first part of "NUMBER · name" titles */
const head = (title: string) => title.split(' · ')[0]!.trim();

/** Where a search hit opens. */
function target(h: Hit): { to: string; params?: Record<string, string>; search?: Record<string, unknown> } {
  switch (h.type) {
    case 'party': return { to: '/parties/$id', params: { id: h.id } };
    case 'product': return { to: '/products/$id', params: { id: h.id } };
    case 'sales_document': return { to: '/sales/$id', params: { id: h.id } };
    case 'purchase_document': return { to: '/purchasing/$id', params: { id: h.id } };
    case 'payment': return { to: '/payments', search: { q: head(h.title) } };
    case 'purchase_request': return { to: '/sourcing/requests/$id', params: { id: h.id } };
    case 'rfq': return { to: '/sourcing/rfqs/$id', params: { id: h.id } };
    case 'journal_entry': return { to: '/accounting', search: { entry: h.id } };
    case 'account': return { to: '/accounting', search: { tab: 'accounts', q: head(h.title).split(' ')[0] } };
    case 'project': return { to: '/projects', search: { id: h.id } };
    case 'lead': case 'opportunity': return { to: '/crm' };
    case 'asset': return { to: '/assets' };
    case 'instrument': return { to: '/collections' };
    case 'supplier_instrument': return { to: '/treasury' };
    case 'lot': return { to: '/inventory', search: { tab: 'genealogy', lot: h.title } };
    case 'serial': return { to: '/inventory', search: { tab: 'stock' } };
    case 'warehouse': return { to: '/inventory', search: { tab: 'warehouses' } };
    case 'work_order': return { to: '/manufacturing' };
    case 'pos_ticket': return { to: '/pos' };
    default: return { to: '/' };
  }
}

const itemClass = 'flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-1.5 text-sm font-normal text-fg data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent';
const groupClass = 'mt-1 text-xs font-semibold text-subtle [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5';

/**
 * Ctrl/Cmd+K: search everything - the records of every module the user may view (customers, suppliers, products, documents, payments, requests,
 * entries, projects, CRM, assets, lots, serials…), the pages, and the create actions. Entries follow the user's permissions.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const shown = useModuleFilter();
  const { me } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const results = useQuery({
    queryKey: ['search', q], enabled: open && q.length >= 2, staleTime: 15_000, retry: false,
    queryFn: () => http.get<SearchResult>('/search', { query: { q } }),
  });

  const close = () => { onOpenChange(false); setText(''); };
  const go = (to: string, search?: Record<string, unknown>, params?: Record<string, string>) => { close(); void navigate({ to, search, params } as never); };
  const openHit = (h: Hit) => {
    const dest = target(h);
    go(dest.to, dest.search, dest.params);
  };

  const needle = text.trim().toLowerCase();
  const match = (label: string) => !needle || label.toLowerCase().includes(needle);
  const pages = NAV.flatMap(groupLinks).filter((i) => (!i.permission || can(me, i.permission)) && shown(i) && match(t(i.label)));
  const create = useMemo(() => [
    can(me, 'invoice.create') && { label: t('dashboard.newInvoice'), to: '/sales/new' },
    can(me, 'purchase.manage') && { label: t('purch.newOrder'), to: '/purchasing/orders/new' },
    can(me, 'purchase.manage') && { label: t('purch.newInvoice'), to: '/purchasing/invoices/new' },
    can(me, 'payment.create') && { label: t('dashboard.newPayment'), to: '/payments/new' },
    can(me, 'party.manage') && { label: t('dashboard.newCustomer'), to: '/parties', search: { new: '1' } },
  ].filter(Boolean) as Array<{ label: string; to: string; search?: Record<string, unknown> }>, [me, t]).filter((c) => match(c.label));
  const groups = q.length >= 2 && results.data?.q === q ? results.data.groups : [];
  const searching = text.trim().length >= 2 && (results.isFetching || q !== text.trim());
  const nothing = !pages.length && !create.length && !groups.length && !searching;

  return (
    <D.Root open={open} onOpenChange={(o) => { if (!o) setText(''); onOpenChange(o); }}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
          <D.Content aria-label={t('nav.commandPlaceholder')} className="animate-in fixed start-1/2 top-[10vh] z-50 w-[min(94vw,640px)] overflow-hidden rounded-xl border border-border bg-surface shadow-lg outline-none ltr:-translate-x-1/2 rtl:translate-x-1/2">
            <D.Title className="sr-only">{t('nav.commandPlaceholder')}</D.Title>
            <D.Description className="sr-only">{t('search.hint')}</D.Description>
            {/* server results are already filtered: cmdk must not filter them again */}
            <Command label={t('nav.commandPlaceholder')} loop shouldFilter={false}>
              <div className="flex items-center gap-2 border-b border-border px-4">
                {searching ? <Loader2 className="size-4 animate-spin text-subtle" aria-hidden /> : <Search className="size-4 text-subtle" aria-hidden />}
                <Command.Input autoFocus value={text} onValueChange={setText} placeholder={t('search.placeholder')} className="h-12 w-full bg-transparent text-base outline-none placeholder:text-subtle md:text-sm" />
              </div>
              <Command.List className="scrollbar-thin max-h-[60vh] overflow-y-auto p-2">
                {text.trim().length === 1 && <p className="px-3 py-2 text-xs text-muted">{t('search.minChars')}</p>}
                {results.isError && <p className="px-3 py-2 text-xs text-danger">{t('search.failed')}</p>}
                {nothing && <p className="px-3 py-8 text-center text-sm text-muted">{t('nav.commandEmpty')}</p>}
                {groups.map((g) => {
                  const Icon = ICONS[g.type] ?? Receipt;
                  return (
                    <Command.Group key={g.type} heading={t(`search.type.${g.type}`)} className={groupClass}>
                      {g.items.map((h) => (
                        <Command.Item key={`${g.type}-${h.id}`} value={`${g.type}-${h.id}`} onSelect={() => openHit(h)} className={itemClass}>
                          <Icon className="size-4 shrink-0" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{h.title}</span>
                            {h.subtitle && <span className="block truncate text-xs text-muted">{h.subtitle}</span>}
                          </span>
                          {h.status && <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted">{t(`status.${h.status}`, { defaultValue: h.status })}</span>}
                          {h.date && <span className="tnum shrink-0 text-xs text-subtle">{h.date}</span>}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  );
                })}
                {pages.length > 0 && (
                  <Command.Group heading={t('nav.go')} className={groupClass}>
                    {pages.map((p) => (
                      <Command.Item key={p.to + p.label} value={`page-${p.to}-${p.label}`} onSelect={() => go(p.to, p.search)} className={itemClass}>
                        <p.icon className="size-4" aria-hidden />{t(p.label)}
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
                {create.length > 0 && (
                  <Command.Group heading={t('nav.createGroup')} className={groupClass}>
                    {create.map((c) => (
                      <Command.Item key={c.label} value={`create-${c.label}`} onSelect={() => go(c.to, c.search)} className={itemClass}>
                        <Plus className="size-4" aria-hidden />{c.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
              </Command.List>
            </Command>
          </D.Content>
        </D.Portal>
    </D.Root>
  );
}
